import { useEffect, useState } from 'react';
import { announceChange } from '../lib/changes';
import { ApiError, api, hourLabel, hoursLabel, slotLabel, type Candidate, type EditOutcome, type Flag, type PeriodShift, type Site } from '../lib/api';
import { addDays, dayLabel } from '../lib/dates';

/**
 * Every way a single shift can be changed, in one place: reassign, retime, move a handover
 * that slipped (D70), remove, add — each with a reason for the audit log, each showing back
 * the errors it would introduce so they can be overridden with a second reason.
 *
 * Lifted out of the week page by D71 so the quarterly grids can offer exactly the same edits.
 * They are the same dialogs, not a second copy: a rule added here reaches every screen at once,
 * which is what went wrong the last time two screens each grew their own idea of a rule.
 */


/**
 * The shift that hands over to this one: same position, ending exactly when it starts, either
 * earlier the same day or the day before through midnight. Mirrors the server's rule (D70) —
 * here only to decide whether to offer the button. The server is the authority and refuses
 * with its own message if the pair does not hold when the change is submitted.
 */
export function handoverPartner(shift: PeriodShift, all: PeriodShift[]): PeriodShift | null {
  if (!shift.start) return null;
  const dayBefore = addDays(shift.date, -1);
  const same = all.filter((r) => r.id !== shift.id && r.positionSlot === shift.positionSlot && r.start && r.end === shift.start);
  return same.find((r) => r.date === shift.date) ?? same.find((r) => r.date === dayBefore && r.crossesMidnight) ?? null;
}

/* ---------- dialogs ---------- */

export function useSubmit(editedBy: string, onDone: (o: EditOutcome) => void) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [introduced, setIntroduced] = useState<Flag[] | null>(null);
  const run = async (reason: string, overrideReason: string | undefined, call: (actor: { editedBy: string; reason: string; overrideReason?: string }) => Promise<EditOutcome>) => {
    if (!editedBy.trim()) return setProblem('Enter your name at the top of the page first — every edit is recorded with who made it.');
    if (!reason.trim()) return setProblem('A reason is required for every edit.');
    setBusy(true);
    setProblem(null);
    try {
      const outcome = await call({ editedBy, reason, ...(overrideReason?.trim() ? { overrideReason } : {}) });
      // Every shift edit passes through here, whichever screen opened the dialog, so this is the
      // one place that can tell the rest of the app (D93). Announced before `onDone` so a screen
      // that reloads on the announcement is already refetching as its own handler runs.
      announceChange({ tools: ['shift_edit'] });
      onDone(outcome);
    } catch (e) {
      const body = e instanceof ApiError && e.status === 422 && e.body && typeof e.body === 'object' ? (e.body as { hard?: boolean; message?: string; introduced?: Flag[] }) : null;
      if (body?.hard) {
        // The credentialing rule (D41): shown as a refusal, never as something to override.
        setIntroduced(null);
        setProblem(body.message ?? 'Not allowed: a physician can only be scheduled at a hospital where they are credentialed.');
      } else if (body?.introduced) {
        setIntroduced(body.introduced);
        setProblem(null);
      } else {
        setProblem(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };
  return { busy, problem, introduced, run, setProblem };
}

export function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function IntroducedBox({ introduced, override, setOverride }: { introduced: Flag[] | null; override: string; setOverride: (v: string) => void }) {
  if (!introduced) return null;
  return (
    <div className="introduced">
      <strong>This change would introduce {introduced.length} error{introduced.length === 1 ? '' : 's'}:</strong>
      <ul>{introduced.map((f, i) => <li key={i}>{f.message}</li>)}</ul>
      <label>To apply it anyway, give an override reason (recorded in the audit log):
        <input value={override} onChange={(e) => setOverride(e.target.value)} placeholder="e.g. John confirmed by phone" />
      </label>
    </div>
  );
}

export function ReasonField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label>Reason
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="e.g. sick day swap, requested by Dr. X" autoFocus />
    </label>
  );
}

export function ReassignDialog({ site, shift, editedBy, periodStart, onClose, onDone }: { site: Site; shift: PeriodShift; editedBy: string; periodStart: string; onClose: () => void; onDone: (o: EditOutcome) => void }) {
  const [cands, setCands] = useState<Candidate[] | null>(null);
  const [pick, setPick] = useState<string | null>(shift.physician?.abbreviation ?? null);
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const { busy, problem, introduced, run } = useSubmit(editedBy, onDone);
  useEffect(() => {
    api.candidates(site.id, shift.positionSlot, shift.date).then(setCands).catch(() => setCands([]));
  }, [site.id, shift.positionSlot, shift.date]);

  return (
    <Modal title={`Reassign ${slotLabel(shift.positionSlot)} on ${dayLabel(shift.date)}`} onClose={onClose}>
      <p className="muted">Currently {shift.physician ? `${shift.physician.firstName} ${shift.physician.lastName}` : 'open'}. Who could fill this — eligible physicians, with anything the rules would object to.</p>
      {cands === null ? <p className="muted">Checking who's eligible…</p> : (
        <ul className="cands">
          <li>
            <label><input type="radio" name="who" checked={pick === null} onChange={() => setPick(null)} /> <em>Leave open</em></label>
          </li>
          {cands.map((c) => (
            <li key={c.abbreviation} className={c.blockers.length ? 'blocked' : c.notes.length ? 'noted' : 'clear'}>
              <label>
                <input type="radio" name="who" checked={pick === c.abbreviation} onChange={() => setPick(c.abbreviation)} />{' '}
                {c.lastName}, {c.firstName}
                {c.blockers.map((b, i) => <span key={`b${i}`} className="why err">{b}</span>)}
                {c.notes.map((n, i) => <span key={`n${i}`} className="why warn">{n}</span>)}
              </label>
            </li>
          ))}
          {cands.length === 0 && <li className="muted">Nobody on the roster is eligible for this slot.</li>}
        </ul>
      )}
      <ReasonField value={reason} onChange={setReason} />
      <IntroducedBox introduced={introduced} override={override} setOverride={setOverride} />
      {problem && <p className="error">{problem}</p>}
      <div className="modal-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="primary" disabled={busy || pick === (shift.physician?.abbreviation ?? null)} onClick={() => run(reason, override, (actor) => api.patchShift(site.id, periodStart, shift.id, { ...actor, physicianId: pick }))}>
          {introduced ? 'Apply anyway' : 'Reassign'}
        </button>
      </div>
    </Modal>
  );
}

export function RetimeDialog({ site, shift, editedBy, periodStart, onClose, onDone }: { site: Site; shift: PeriodShift; editedBy: string; periodStart: string; onClose: () => void; onDone: (o: EditOutcome) => void }) {
  const [onCall, setOnCall] = useState(shift.start === null);
  const [start, setStart] = useState(shift.start ?? '07:00');
  const [end, setEnd] = useState(shift.end ?? '23:00');
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const { busy, problem, introduced, run } = useSubmit(editedBy, onDone);
  return (
    <Modal title={`Retime ${slotLabel(shift.positionSlot)} on ${dayLabel(shift.date)}`} onClose={onClose}>
      <label><input type="checkbox" checked={onCall} onChange={(e) => setOnCall(e.target.checked)} /> Untimed on-call (no hours)</label>
      {!onCall && (
        <div className="times">
          <label>Start <input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label>End <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
          <span className="muted">{start && end ? hoursLabel(start, end, end <= start) : ''}{start && end && end <= start ? ' (ends next day)' : ''}</span>
        </div>
      )}
      <ReasonField value={reason} onChange={setReason} />
      <IntroducedBox introduced={introduced} override={override} setOverride={setOverride} />
      {problem && <p className="error">{problem}</p>}
      <div className="modal-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="primary" disabled={busy} onClick={() => run(reason, override, (actor) => api.patchShift(site.id, periodStart, shift.id, { ...actor, start: onCall ? null : start, end: onCall ? null : end }))}>
          {introduced ? 'Apply anyway' : 'Retime'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * A handover that slipped (D70). The oncoming physician was delayed, so the one on duty stayed
 * to cover. One time, two shifts: the outgoing one runs longer, the incoming one starts later.
 * Doing it as a pair is the point — two separate retimes would leave a gap or an overlap on the
 * timesheet in between, and would need two reasons for one event.
 */
export function HandoverDialog({ site, shift, outgoing, editedBy, periodStart, onClose, onDone }: { site: Site; shift: PeriodShift; outgoing: PeriodShift; editedBy: string; periodStart: string; onClose: () => void; onDone: (o: EditOutcome) => void }) {
  const [at, setAt] = useState(shift.start ?? '');
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const { busy, problem, introduced, run } = useSubmit(editedBy, onDone);
  const name = (x: PeriodShift) => (x.physician ? `${x.physician.lastName}, ${x.physician.firstName}` : 'open');
  const span = (start: string | null, end: string | null) => (start && end ? hoursLabel(start, end, end <= start) : 'on call');
  const moved = at && at !== shift.start && at !== outgoing.start && at !== shift.end;
  return (
    <Modal title={`Handover — ${slotLabel(shift.positionSlot)} on ${dayLabel(shift.date)}`} onClose={onClose}>
      <p className="muted small">
        Cover changes hands at {hourLabel(shift.start!)}. Use this when it actually happened at a different time — a delayed flight, say,
        and whoever was on stayed to cover. Both shifts move together, so no gap or overlap is left behind and both timesheets
        come out right.
      </p>
      <table className="shifts-table handover">
        <tbody>
          <tr>
            <td className="slot">Stays on</td>
            <td className="who">{name(outgoing)}</td>
            <td className="hours">{span(outgoing.start, outgoing.end)}{moved && <> → <strong>{span(outgoing.start, at)}</strong></>}</td>
          </tr>
          <tr>
            <td className="slot">Comes on</td>
            <td className="who">{name(shift)}</td>
            <td className="hours">{span(shift.start, shift.end)}{moved && <> → <strong>{span(at, shift.end)}</strong></>}</td>
          </tr>
        </tbody>
      </table>
      <label>Cover actually changed at <input type="time" value={at} onChange={(e) => setAt(e.target.value)} autoFocus /></label>
      <ReasonField value={reason} onChange={setReason} />
      <IntroducedBox introduced={introduced} override={override} setOverride={setOverride} />
      {problem && <p className="error">{problem}</p>}
      <div className="modal-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="primary" disabled={busy || !moved} onClick={() => run(reason, override, (actor) => api.moveHandover(site.id, periodStart, shift.id, { ...actor, at }))}>
          {introduced ? 'Apply anyway' : 'Move the handover'}
        </button>
      </div>
    </Modal>
  );
}

export function RemoveDialog({ site, shift, editedBy, periodStart, onClose, onDone }: { site: Site; shift: PeriodShift; editedBy: string; periodStart: string; onClose: () => void; onDone: (o: EditOutcome) => void }) {
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const { busy, problem, introduced, run } = useSubmit(editedBy, onDone);
  return (
    <Modal title={`Remove ${slotLabel(shift.positionSlot)} on ${dayLabel(shift.date)}`} onClose={onClose}>
      <p>{shift.physician ? `${shift.physician.firstName} ${shift.physician.lastName}` : 'This open row'} · {shift.start ? hoursLabel(shift.start, shift.end!, shift.crossesMidnight) : 'on call'}. The row is deleted; the audit log keeps what it was.</p>
      <ReasonField value={reason} onChange={setReason} />
      <IntroducedBox introduced={introduced} override={override} setOverride={setOverride} />
      {problem && <p className="error">{problem}</p>}
      <div className="modal-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="danger" disabled={busy} onClick={() => run(reason, override, (actor) => api.removeShift(site.id, periodStart, shift.id, actor))}>
          {introduced ? 'Remove anyway' : 'Remove'}
        </button>
      </div>
    </Modal>
  );
}

export function AddDialog({ site, dates, existingDates, editedBy, periodStart, slot: initialSlot, onClose, onDone }: { site: Site; dates: string[]; existingDates: string[]; editedBy: string; periodStart: string; /** Preselected position — set when the add started from that row of the grid (D71). */ slot?: string; onClose: () => void; onDone: (o: EditOutcome) => void }) {
  const [date, setDate] = useState(existingDates[0] ?? dates[0]);
  const [slot, setSlot] = useState(initialSlot ?? site.slots[0] ?? '');
  const [onCall, setOnCall] = useState(false);
  const [start, setStart] = useState('07:00');
  const [end, setEnd] = useState('23:00');
  const [cands, setCands] = useState<Candidate[] | null>(null);
  const [pick, setPick] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const { busy, problem, introduced, run } = useSubmit(editedBy, onDone);
  useEffect(() => {
    setCands(null);
    if (date && slot) api.candidates(site.id, slot, date).then(setCands).catch(() => setCands([]));
  }, [site.id, slot, date]);

  return (
    <Modal title="Add a shift" onClose={onClose}>
      <div className="times">
        <label>Day
          <select value={date} onChange={(e) => setDate(e.target.value)}>
            {dates.map((d) => <option key={d} value={d}>{dayLabel(d)}</option>)}
          </select>
        </label>
        <label>Position
          <select value={slot} onChange={(e) => setSlot(e.target.value)}>
            {site.slots.map((s) => <option key={s} value={s}>{slotLabel(s)}</option>)}
          </select>
        </label>
      </div>
      <label><input type="checkbox" checked={onCall} onChange={(e) => setOnCall(e.target.checked)} /> Untimed on-call (no hours)</label>
      {!onCall && (
        <div className="times">
          <label>Start <input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label>End <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        </div>
      )}
      <p className="muted">Who takes it:</p>
      {cands === null ? <p className="muted">Checking who's eligible…</p> : (
        <ul className="cands">
          <li><label><input type="radio" name="who" checked={pick === null} onChange={() => setPick(null)} /> <em>Leave open</em></label></li>
          {cands.map((c) => (
            <li key={c.abbreviation} className={c.blockers.length ? 'blocked' : c.notes.length ? 'noted' : 'clear'}>
              <label>
                <input type="radio" name="who" checked={pick === c.abbreviation} onChange={() => setPick(c.abbreviation)} /> {c.lastName}, {c.firstName}
                {c.blockers.map((b, i) => <span key={`b${i}`} className="why err">{b}</span>)}
                {c.notes.map((n, i) => <span key={`n${i}`} className="why warn">{n}</span>)}
              </label>
            </li>
          ))}
        </ul>
      )}
      <ReasonField value={reason} onChange={setReason} />
      <IntroducedBox introduced={introduced} override={override} setOverride={setOverride} />
      {problem && <p className="error">{problem}</p>}
      <div className="modal-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="primary" disabled={busy || !slot} onClick={() => run(reason, override, (actor) => api.addShift(site.id, periodStart, { ...actor, date, positionSlot: slot, physicianId: pick, start: onCall ? null : start, end: onCall ? null : end }))}>
          {introduced ? 'Add anyway' : 'Add'}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- editing a shift where it stands (D71) ---------- */

/** A week a shift can still be changed in. Matches EDITABLE on the server. */
export const EDITABLE_STATUS = new Set(['draft', 'finalized', 'published']);

/**
 * Every edit for one shift, reached from a grid cell rather than the week page (D71).
 *
 * A grid cell is small and a name in it says nothing about what can be done to it, so the
 * first step names the shift in full — who, what hours, which week and what state that week
 * is in — and only then offers the changes. Each one hands off to the very dialog the week
 * page opens, so there is one way each edit behaves and one place to change it.
 */
export function ShiftEditor({
  site, shift, siblings, editedBy, periodStart, weekStatus, onClose, onDone, onOpenWeek,
}: {
  site: Site;
  shift: PeriodShift;
  /** Every shift at this hospital in view, so a handover can find its other half. */
  siblings: PeriodShift[];
  editedBy: string;
  periodStart: string;
  weekStatus: string;
  onClose: () => void;
  onDone: (o: EditOutcome, what: string) => void;
  onOpenWeek: () => void;
}) {
  const [step, setStep] = useState<'menu' | 'reassign' | 'retime' | 'handover' | 'remove'>('menu');
  const outgoing = handoverPartner(shift, siblings);
  const common = { site, shift, editedBy, periodStart, onClose };

  if (step === 'reassign') return <ReassignDialog {...common} onDone={(o) => onDone(o, 'Reassigned')} />;
  if (step === 'retime') return <RetimeDialog {...common} onDone={(o) => onDone(o, 'Retimed')} />;
  if (step === 'remove') return <RemoveDialog {...common} onDone={(o) => onDone(o, 'Removed')} />;
  if (step === 'handover' && outgoing) return <HandoverDialog {...common} outgoing={outgoing} onDone={(o) => onDone(o, 'Handover moved')} />;

  const editable = EDITABLE_STATUS.has(weekStatus);
  return (
    <Modal title={`${slotLabel(shift.positionSlot)} — ${dayLabel(shift.date)}`} onClose={onClose}>
      <table className="shifts-table handover">
        <tbody>
          <tr>
            <td className="slot">Who</td>
            <td className="who">{shift.physician ? `${shift.physician.lastName}, ${shift.physician.firstName}` : <em>open</em>}</td>
          </tr>
          <tr>
            <td className="slot">Hours</td>
            <td className="hours">{shift.start && shift.end ? hoursLabel(shift.start, shift.end, shift.crossesMidnight) : 'on call'}</td>
          </tr>
          <tr>
            <td className="slot">Week</td>
            <td>{dayLabel(periodStart)} · <span className="pill tiny">{weekStatus}</span></td>
          </tr>
        </tbody>
      </table>
      {!editable && <p className="muted">A {weekStatus} week cannot be changed here.</p>}
      {editable && (
        <div className="cellactions">
          <button type="button" onClick={() => setStep('reassign')}>Reassign</button>
          <button type="button" onClick={() => setStep('retime')}>Retime</button>
          {outgoing && (
            <button type="button" onClick={() => setStep('handover')} title="Cover changed hands at a different time — a delayed flight, say. Moves both shifts together.">Handover</button>
          )}
          <button type="button" className="danger" onClick={() => setStep('remove')}>Remove</button>
        </div>
      )}
      <div className="modal-actions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" onClick={onOpenWeek}>Open the whole week</button>
      </div>
    </Modal>
  );
}
